import { useEffect, useMemo, useState } from "react";

// Category filter options. `label` is what the user sees and matches the
// user's requested copy; `value` is the raw value in companies.json.
const CATEGORY_OPTIONS = [
  { label: "Brain", value: "Brain" },
  { label: "Body", value: "Body" },
  { label: "Body_Solar Energy", value: "Body_Solar Energy" },
  { label: "ai Adoption / Application", value: "ai Adoption / Application" },
  {
    label: "ai Application - Not Tech Sector",
    value: "ai Adoption / Application - Not Tech Sector",
  },
  { label: "Solar", value: "Solar" },
];

const MOMENTUM_OPTIONS = [
  {
    key: "GREEN_UP_UP",
    label: "GREEN  1M (+)  YTD (+)",
    dot: "green",
    value: "GREEN",
  },
  {
    key: "GREEN_UP_DOWN",
    label: "GREEN  1M (+),  YTD (-)",
    dot: "green",
    value: "GREEN 1M (+), YTD(-)",
  },
  {
    key: "RED_DOWN_DOWN",
    label: "RED  1M (-),  YTD (-)",
    dot: "red",
    value: "RED",
  },
];

const YTD_OPTIONS = [
  {
    key: "YTD_GREEN",
    label: "GREEN",
    dot: "green",
    value: "GREEN",
  },
  {
    key: "YTD_RED",
    label: "RED",
    dot: "red",
    value: "RED",
  },
];

const PAGE_SIZE = 50;

export default function Companies() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sector, setSector] = useState("");
  const [industry, setIndustry] = useState("");
  const [category, setCategory] = useState(""); // raw value
  const [momentum, setMomentum] = useState(""); // raw value
  const [ytdFilter, setYtdFilter] = useState(""); // YTD Jun18 filter
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    fetch("/companies.json")
      .then((r) => r.json())
      .then((d) => {
        setRows(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const sectors = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.sector).filter(Boolean))).sort(),
    [rows],
  );

  const industries = useMemo(() => {
    const pool = sector ? rows.filter((r) => r.sector === sector) : rows;
    return Array.from(
      new Set(pool.map((r) => r.industry).filter(Boolean)),
    ).sort();
  }, [rows, sector]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (sector && r.sector !== sector) return false;
      if (industry && r.industry !== industry) return false;
      if (category && r.category !== category) return false;
      if (momentum && r.momentum !== momentum) return false;
      if (ytdFilter && r.ytd_jun18 !== ytdFilter) return false;
      if (q) {
        const hay = `${r.symbol || ""} ${r.name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, query, sector, industry, category, momentum, ytdFilter]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [query, sector, industry, category, momentum, ytdFilter]);

  const counts = useMemo(() => {
    const c = {
      total: filtered.length,
      green_up_up: 0,
      green_up_down: 0,
      red_down_down: 0,
      ytd_green: 0,
      ytd_red: 0,
    };
    for (const r of filtered) {
      if (r.momentum === "GREEN") c.green_up_up++;
      else if (r.momentum === "GREEN 1M (+), YTD(-)") c.green_up_down++;
      else if (r.momentum === "RED") c.red_down_down++;
      if (r.ytd_jun18 === "GREEN") c.ytd_green++;
      else if (r.ytd_jun18 === "RED") c.ytd_red++;
    }
    return c;
  }, [filtered]);

  const reset = () => {
    setQuery("");
    setSector("");
    setIndustry("");
    setCategory("");
    setMomentum("");
    setYtdFilter("");
  };

  return (
    <main
      data-testid="companies-page"
      className="max-w-[1280px] mx-auto px-8 pt-16 pb-8"
    >
      {/* Hero */}
      <section data-testid="hero" className="fade-in mb-20">
        <div className="eyebrow mb-6" data-testid="hero-eyebrow">
          A Reference Page
        </div>
        <p
          data-testid="hero-description"
          style={{
            color: "var(--pord-teal)",
            fontSize: "clamp(22px, 2.4vw, 32px)",
            lineHeight: 1.4,
            fontWeight: 300,
            maxWidth: 900,
            letterSpacing: "-0.005em",
          }}
        >
          A browsable view categorized as Brain, Body, or Application of
          artificial intelligence, and marked by recent momentum.
        </p>
        <p
          data-testid="hero-asof"
          className="label-cap"
          style={{
            color: "var(--pord-muted)",
            marginTop: 20,
          }}
        >
          Info as of 06·18·2026
        </p>
        <p
          style={{
            marginTop: 8,
            fontSize: 10,
            letterSpacing: "0.08em",
          }}
        >
          <span style={{ color: "var(--pord-muted)" }}>Source: </span>
          <a
            href="https://stockanalysis.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="source-stockanalysis-page1"
            style={{ color: "var(--pord-sage)", textDecoration: "none", borderBottom: "1px solid rgba(154,173,168,0.3)" }}
          >
            StockAnalysis
          </a>
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            data-testid="companies-download-btn"
            href="/Pord-Look-Up-Sheet.pdf"
            download
            className="pord-btn pord-btn-ghost"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Look-Up Sheet (PDF)
          </a>
          <span
            className="label-cap"
            style={{ color: "var(--pord-muted)" }}
          >
            · {rows.length.toLocaleString()} entries · PDF
          </span>
        </div>
      </section>

      {/* Search */}
      <section data-testid="search-section" className="fade-in-d1 mb-12">
        <label
          className="label-cap"
          style={{
            color: "var(--pord-sage)",
            display: "block",
            marginBottom: 8,
          }}
        >
          Search
        </label>
        <input
          data-testid="search-input"
          className="pord-input"
          placeholder="Try NVDA, Palantir, semiconductors…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </section>

      {/* Filters */}
      <section data-testid="filters" className="fade-in-d2 mb-12">
        <div className="flex items-baseline justify-between mb-5">
          <div
            className="label-cap"
            style={{ color: "var(--pord-sage)" }}
            data-testid="filters-heading"
          >
            Filters
          </div>
          {(sector || industry || category || momentum || ytdFilter || query) && (
            <button
              data-testid="reset-filters"
              onClick={reset}
              className="label-cap"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--pord-teal)",
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-start gap-3 mb-6">
          <span
            className="label-cap mt-2 mr-2"
            style={{ color: "var(--pord-muted)", minWidth: 84 }}
          >
            Category
          </span>
          <div className="flex flex-wrap gap-3 flex-1">
            {CATEGORY_OPTIONS.map((opt) => {
              const tid = opt.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
              return (
                <button
                  key={opt.value}
                  data-testid={`category-chip-${tid}`}
                  className="pord-chip"
                  data-active={category === opt.value}
                  onClick={() =>
                    setCategory(category === opt.value ? "" : opt.value)
                  }
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-3 mb-6">
          <span
            className="label-cap mt-2 mr-2"
            style={{ color: "var(--pord-muted)", minWidth: 84 }}
          >
            Momentum : MAY29
          </span>
          <div className="flex flex-wrap gap-3 flex-1">
            {MOMENTUM_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                data-testid={`momentum-chip-${opt.key.toLowerCase()}`}
                className="pord-chip"
                data-active={momentum === opt.value}
                onClick={() =>
                  setMomentum(momentum === opt.value ? "" : opt.value)
                }
              >
                <span
                  className={opt.dot === "green" ? "dot dot-green" : "dot dot-red"}
                />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-3 mb-6">
          <span
            className="label-cap mt-2 mr-2"
            style={{ color: "var(--pord-muted)", minWidth: 84 }}
          >
            YTD : JUN18
          </span>
          <div className="flex flex-wrap gap-3 flex-1">
            {YTD_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                data-testid={`ytd-chip-${opt.key.toLowerCase()}`}
                className="pord-chip"
                data-active={ytdFilter === opt.value}
                onClick={() =>
                  setYtdFilter(ytdFilter === opt.value ? "" : opt.value)
                }
              >
                <span
                  className={opt.dot === "green" ? "dot dot-green" : "dot dot-red"}
                />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <span
            className="label-cap mr-2"
            style={{ color: "var(--pord-muted)", minWidth: 84 }}
          >
            Sector
          </span>
          <select
            data-testid="sector-select"
            className="pord-select"
            value={sector}
            onChange={(e) => {
              setSector(e.target.value);
              setIndustry("");
            }}
          >
            <option value="">All sectors</option>
            {sectors.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <span
            className="label-cap ml-4 mr-2"
            style={{ color: "var(--pord-muted)" }}
          >
            Industry
          </span>
          <select
            data-testid="industry-select"
            className="pord-select"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
          >
            <option value="">All industries</option>
            {industries.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Summary strip */}
      <section data-testid="summary-strip" className="fade-in-d3 mb-10">
        <div className="divider mb-8" />
        <div
          className="label-cap mb-4"
          style={{ color: "var(--pord-sage)" }}
        >
          Momentum : MAY29
        </div>
        <div
          className="grid ref-stats-grid"
          style={{
            gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 32,
          }}
        >
          <Stat
            label="Showing"
            value={counts.total.toLocaleString()}
            testid="stat-total"
          />
          <Stat
            label="GREEN · 1M+ YTD+"
            value={counts.green_up_up.toLocaleString()}
            testid="stat-green-up-up"
            tone="green"
          />
          <Stat
            label="GREEN · 1M+ YTD-"
            value={counts.green_up_down.toLocaleString()}
            testid="stat-green-up-down"
            tone="green"
          />
          <Stat
            label="RED · 1M- YTD-"
            value={counts.red_down_down.toLocaleString()}
            testid="stat-red-down-down"
            tone="red"
          />
        </div>
        <div className="divider mt-8 mb-8" />
        <div
          className="label-cap mb-4"
          style={{ color: "var(--pord-sage)" }}
        >
          YTD : JUN18
        </div>
        <div
          className="grid ref-stats-grid"
          style={{
            gridTemplateColumns: "repeat(3, minmax(0,1fr))",
            gap: 32,
          }}
        >
          <Stat
            label="Showing"
            value={counts.total.toLocaleString()}
            testid="stat-ytd-total"
          />
          <Stat
            label="GREEN"
            value={counts.ytd_green.toLocaleString()}
            testid="stat-ytd-green"
            tone="green"
          />
          <Stat
            label="RED"
            value={counts.ytd_red.toLocaleString()}
            testid="stat-ytd-red"
            tone="red"
          />
        </div>
        <div className="divider mt-8" />
      </section>

      {/* Table */}
      <section data-testid="results-section">
        {loading ? (
          <div
            data-testid="loading"
            style={{ color: "var(--pord-muted)", padding: "40px 0" }}
          >
            Reading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="empty-state"
            style={{ color: "var(--pord-muted)", padding: "60px 0" }}
          >
            <div
              className="label-cap mb-3"
              style={{ color: "var(--pord-sage)" }}
            >
              Nothing here
            </div>
            <div style={{ fontSize: 20, color: "var(--pord-text)" }}>
              No companies match these filters.
            </div>
          </div>
        ) : (
          <>
            <div
              className="hidden md:grid label-cap"
              style={{
                gridTemplateColumns:
                  "90px minmax(0,2fr) 1fr 1.2fr 1.4fr 1.2fr 1fr",
                gap: 20,
                color: "var(--pord-sage)",
                padding: "0 0 16px 0",
              }}
              data-testid="table-header"
            >
              <div>Symbol</div>
              <div>Company</div>
              <div>Sector</div>
              <div>Industry</div>
              <div>Category</div>
              <div>MAY29</div>
              <div>YTD JUN18</div>
            </div>

            <div className="divider" />

            <ul data-testid="companies-list" className="ref-data-list">
              {filtered.slice(0, visible).map((r, i) => (
                <li
                  key={`${r.symbol}-${i}`}
                  data-testid={`row-${r.symbol}`}
                  className="row-hover"
                  style={{
                    borderBottom: "1px solid rgba(184,212,208,0.06)",
                  }}
                >
                  <div
                    className="grid"
                    style={{
                      gridTemplateColumns:
                        "90px minmax(0,2fr) 1fr 1.2fr 1.4fr 1.2fr 1fr",
                      gap: 20,
                      padding: "20px 0",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "DM Sans, sans-serif",
                        fontWeight: 400,
                        letterSpacing: "0.04em",
                        color: "var(--pord-teal)",
                        fontSize: 14,
                      }}
                    >
                      {r.symbol}
                    </div>
                    <div
                      style={{
                        color: "var(--pord-text)",
                        fontSize: 16,
                        fontWeight: 300,
                      }}
                    >
                      {r.name}
                    </div>
                    <div
                      style={{ color: "var(--pord-muted)", fontSize: 13 }}
                    >
                      {r.sector || "—"}
                    </div>
                    <div
                      style={{ color: "var(--pord-muted)", fontSize: 13 }}
                    >
                      {r.industry || "—"}
                    </div>
                    <div>
                      <CategoryBadge category={r.category} />
                    </div>
                    <div>
                      <MomentumBadge momentum={r.momentum} />
                    </div>
                    <div>
                      <YtdBadge ytd={r.ytd_jun18} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {visible < filtered.length && (
              <div className="mt-12 mb-4 flex items-center gap-6">
                <button
                  data-testid="load-more"
                  className="pord-btn pord-btn-ghost"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                >
                  Show more
                </button>
                <span
                  className="label-cap"
                  style={{ color: "var(--pord-muted)" }}
                >
                  {visible.toLocaleString()} of{" "}
                  {filtered.length.toLocaleString()}
                </span>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function Stat({ label, value, testid, tone }) {
  const valueColor =
    tone === "green"
      ? "#bcd9c2"
      : tone === "red"
        ? "#e0b6b6"
        : "var(--pord-text)";
  return (
    <div data-testid={testid}>
      <div
        className="label-cap"
        style={{ color: "var(--pord-sage)", marginBottom: 8 }}
      >
        {label}
      </div>
      <div className="headline" style={{ fontSize: 28, color: valueColor }}>
        {value}
      </div>
    </div>
  );
}

const CATEGORY_STYLE = {
  Brain: { fg: "var(--pord-teal)", bd: "rgba(184,212,208,0.45)" },
  Body: { fg: "var(--pord-sage)", bd: "rgba(154,173,168,0.45)" },
  "Body_Solar Energy": {
    fg: "#cfe0bf",
    bd: "rgba(207,224,191,0.35)",
  },
  "ai Adoption / Application": {
    fg: "#cfd8d4",
    bd: "rgba(207,216,212,0.30)",
  },
  "ai Adoption / Application - Not Tech Sector": {
    fg: "#b8c4c1",
    bd: "rgba(184,196,193,0.25)",
  },
  Solar: { fg: "#e0d4a8", bd: "rgba(224,212,168,0.35)" },
};

const CATEGORY_DISPLAY = {
  "ai Adoption / Application - Not Tech Sector":
    "ai Application · Not Tech",
  "ai Adoption / Application": "ai Adoption / Application",
  "Body_Solar Energy": "Body · Solar Energy",
};

function CategoryBadge({ category }) {
  if (!category) {
    return <span style={{ color: "var(--pord-muted)", fontSize: 13 }}>—</span>;
  }
  const t = CATEGORY_STYLE[category] || CATEGORY_STYLE["ai Adoption / Application"];
  const display = CATEGORY_DISPLAY[category] || category;
  return (
    <span
      data-testid={`cat-badge-${category
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")}`}
      style={{
        display: "inline-block",
        padding: "5px 11px",
        borderRadius: 999,
        border: `1px solid ${t.bd}`,
        color: t.fg,
        fontSize: 10,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {display}
    </span>
  );
}

function MomentumBadge({ momentum }) {
  if (!momentum) return <span style={{ color: "var(--pord-muted)" }}>—</span>;
  let dot = "green";
  let label = "";
  let color = "#bcd9c2";
  let testidKey = "";
  if (momentum === "GREEN") {
    label = "1M+ YTD+";
    dot = "green";
    color = "#bcd9c2";
    testidKey = "green-up-up";
  } else if (momentum === "GREEN 1M (+), YTD(-)") {
    label = "1M+ YTD-";
    dot = "green";
    color = "#bcd9c2";
    testidKey = "green-up-down";
  } else if (momentum === "RED") {
    label = "1M- YTD-";
    dot = "red";
    color = "#e0b6b6";
    testidKey = "red-down-down";
  }
  return (
    <span
      data-testid={`momentum-badge-${testidKey}`}
      className="flex items-center gap-2"
      style={{
        color,
        fontSize: 11,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
      }}
    >
      <span className={dot === "green" ? "dot dot-green" : "dot dot-red"} />
      {label}
    </span>
  );
}

function YtdBadge({ ytd }) {
  if (!ytd) return <span style={{ color: "var(--pord-muted)" }}>—</span>;
  const isGreen = ytd === "GREEN";
  return (
    <span
      data-testid={`ytd-badge-${isGreen ? "green" : "red"}`}
      className="flex items-center gap-2"
      style={{
        color: isGreen ? "#bcd9c2" : "#e0b6b6",
        fontSize: 11,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
      }}
    >
      <span className={isGreen ? "dot dot-green" : "dot dot-red"} />
      {ytd}
    </span>
  );
}
